/**
 * Derivation layer: raw `ItemHistoryEntry` → `MovementRow` view model.
 *
 * This is the substance of the feature and is NOT throwaway — real data flows through
 * the same code once `useItemHistoryMock` is swapped for the generated query hook. It
 * computes the fields the grid renders but the raw entry does not carry: signed
 * direction/delta, a plain-language label, a resolved counterparty, and the degraded-state
 * flags.
 */
import { getCostCapturePurchaseOrderDetailUrl } from 'config/host'
import {
	ItemHistoryEntry,
	ItemHistorySourceType,
	MovementCounterparty,
	MovementDirection,
	MovementFlag,
	MovementRow,
	MovementStatus,
} from './types'

/** Plain-language labels, never the raw enum. Unmapped types degrade gracefully. */
const TYPE_LABELS: Record<string, string> = {
	PURCHASE_ORDER_TO_STORE: 'Received on purchase order',
	STORE_TO_PURCHASE_ORDER: 'Returned on purchase order',
	CREDIT_PURCHASE_TO_STORE: 'Received on credit purchase',
	STORE_TO_WORK_ORDER: 'Issued to work order',
	WORK_ORDER_TO_STORE: 'Returned from work order',
	// STORE_TO_STORE is the only transfer type the API emits; its label is computed from
	// direction + transfer-order presence in deriveMovementRow (this value is a fallback).
	STORE_TO_STORE: 'Transferred out (legacy)',
	STORE_TO_RETAIL_SALE: 'Sold at retail',
	RETAIL_SALE_TO_STORE: 'Returned from retail sale',
	STORE_TO_CREDIT_PURCHASE: 'Returned on credit purchase',
	STORE_TO_DIRECT_SHIPMENT: 'Shipped direct',
	DIRECT_SHIPMENT_TO_STORE: 'Returned from direct shipment',
	STORE_TO_ORDER: 'Shipped on eCommerce order',
	ORDER_TO_STORE: 'Returned from eCommerce order',
	STORE_TO_RENTAL_RETAIL_SALE: 'Sold on rental retail sale',
	// Bulk imports and reconciliation against an external system. No counterparty is
	// recorded for any of them.
	BATCH_ADD_TO_STORE: 'Added in bulk',
	BATCH_SYNC_TO_STORE: 'Bulk sync correction',
	STORE_TO_BATCH_SYNC: 'Bulk sync correction',
	ASSET_TO_STORE: 'Converted from asset (MTT unload)',
	STORE_TO_ASSET: 'Converted to asset (MTT load)',
	// Both conversion legs get a direction- and merge-aware label below; the counterpart
	// product itself lands in the Reference column. These values are the fallback for a
	// conversion whose sibling leg could not be resolved.
	STORE_TO_CONVERSION: 'Converted out',
	CONVERSION_TO_STORE: 'Converted in',
	// The real manual adjustments: a MANUAL_ADJUSTMENT source carries the reason
	// (Found Items, Damaged, Missing, …), surfaced as the counterparty.
	ADJUST_TO_STORE: 'Manual adjustment',
	STORE_TO_ADJUST: 'Manual adjustment',
	STORE_TO_LOSS: 'Inventory loss',
	STORE_TO_OBSOLETE: 'Marked obsolete',
	PROVIDER_TO_STORE: 'Received from provider',
	STORE_TO_PROVIDER: 'Returned to provider',
	// The real rental types. These move owned quantity like any other movement — the API
	// applies no rental special case to the signed change — so they are NOT zero-delta
	// custody moves. Per-unit custody tracking is a separate API gap.
	STORE_TO_RENTAL: 'Placed on rental',
	RENTAL_TO_STORE: 'Returned from rental',
	STORE_TO_RENTAL_LOSS: 'Lost on rental',
}

/** Types that can no longer be created but whose historical rows still matter. */
const DEPRECATED_TYPES = new Set( [
	'STORE_TO_OBSOLETE',
	'STORE_TO_RENTAL',
	'RENTAL_TO_STORE',
] )

const LONG_MEMO_THRESHOLD = 96

function formatActor( actor: ItemHistoryEntry['modifiedByUser'] ): string | null {
	if ( !actor ) {
		return null
	}
	const name = [ actor.firstName, actor.lastName ].filter( Boolean ).join( ' ' ).trim()
	if ( name ) {
		return name
	}
	if ( actor.email ) {
		// Title-case the local part of an email, or pass a non-email system id through.
		if ( actor.email.includes( '@' ) ) {
			return actor.email
				.split( '@' )[ 0 ]
				.split( '.' )
				.map( ( part ) => part.charAt( 0 ).toUpperCase() + part.slice( 1 ) )
				.join( ' ' )
		}
		return actor.email
	}
	return null
}

function resolveCounterparty( entry: ItemHistoryEntry ): { counterparty: MovementCounterparty, flags: MovementFlag[] } {
	const flags: MovementFlag[] = []
	const { source, sourceType, sourceId, customId } = entry

	if ( source ) {
		switch ( source.__typename ) {
			case 'PurchaseOrder': {
				// Show the human PO number when resolved; otherwise a generic label rather than the
				// raw id. Either way link by id, which is how CostCapture routes PO detail.
				const display = source.purchaseOrderNumber ? `PO-${source.purchaseOrderNumber}` : 'Purchase order'
				return { counterparty: { display, href: getCostCapturePurchaseOrderDetailUrl( source.id ), resolved: true, missing: false }, flags }
			}
			case 'ManualTransferOrder':
			case 'SystemTransferOrder':
				return { counterparty: { display: `TO-${source.id}`, to: `/transfer-orders/${source.id}`, resolved: true, missing: false }, flags }
			case 'InventoryLocation':
				return { counterparty: { display: source.name, resolved: true, missing: false }, flags }
			case 'Provider':
				return { counterparty: { display: source.name, resolved: true, missing: false }, flags }
			case 'ManualAdjustment':
				return { counterparty: { display: source.reason ?? 'Manual adjustment', resolved: true, missing: false }, flags }
			case 'StorePart': {
				// The other leg of a conversion/merge, scoped to this location, so it links like
				// any other item. A merge archives the absorbed item — the link still resolves,
				// but say so rather than leaving the reader to wonder why it looks retired.
				if ( source.isArchived ) {
					flags.push( 'archived_counterparty' )
				}
				const display = source.partName ?? `Item ${source.storePartId}`
				return { counterparty: { display, to: `/item/${source.storePartId}`, resolved: true, missing: false }, flags }
			}
			case 'CreditPurchaseReference':
				flags.push( 'id_only' )
				return { counterparty: { display: `Credit purchase ${source.id}`, resolved: false, missing: false }, flags }
			case 'RetailSaleReference':
				flags.push( 'id_only' )
				return { counterparty: { display: `Retail sale ${source.id}`, resolved: false, missing: false }, flags }
			case 'WorkOrderReference':
				flags.push( 'id_only' )
				return { counterparty: { display: `WO-${source.id}`, resolved: false, missing: false }, flags }
			case 'OrderReference':
				flags.push( 'id_only' )
				return { counterparty: { display: `Order ${source.id}`, resolved: false, missing: false }, flags }
			case 'Asset':
				flags.push( 'id_only' )
				return { counterparty: { display: `Asset ${source.id}`, resolved: false, missing: false }, flags }
			default:
				break
		}
	}

	// No resolved object. A human-readable `customId` is the next-best reference.
	if ( customId ) {
		flags.push( sourceType === ItemHistorySourceType.CreditPurchase ? 'unresolved_counterparty' : 'id_only' )
		return { counterparty: { display: customId, resolved: false, missing: false }, flags }
	}

	// A raw id with a known kind but no lookup available.
	if ( sourceType && sourceId ) {
		flags.push( 'id_only' )
		return { counterparty: { display: `${sourceType} ${sourceId}`, resolved: false, missing: false }, flags }
	}

	// Nothing was ever recorded (direct shipment, rental retail sale, …).
	flags.push( 'no_counterparty_recorded' )
	return { counterparty: { display: 'No reference recorded', resolved: false, missing: true }, flags }
}

function resolveDirection( entry: ItemHistoryEntry, recognized: boolean ): { direction: MovementDirection, flags: MovementFlag[] } {
	const flags: MovementFlag[] = []
	const type = entry.transactionType

	if ( recognized ) {
		if ( entry.quantityChange > 0 ) {
			return { direction: 'in', flags }
		}
		if ( entry.quantityChange < 0 ) {
			return { direction: 'out', flags }
		}
		return { direction: 'unchanged', flags }
	}

	// Unrecognized type: fall back to the naming convention, else give up on direction.
	flags.push( 'unrecognized_type' )
	if ( type.endsWith( '_TO_STORE' ) ) {
		flags.push( 'direction_inferred_from_name' )
		return { direction: 'in', flags }
	}
	if ( type.startsWith( 'STORE_TO_' ) ) {
		flags.push( 'direction_inferred_from_name' )
		return { direction: 'out', flags }
	}
	flags.push( 'indeterminate_direction' )
	return { direction: 'unknown', flags }
}

/**
 * Only COMPLETED, CANCELLED and PROCESSING reach an item's history. Anything else is
 * treated as completed rather than hidden — an unknown status must not silently suppress
 * a row that did move stock.
 */
function resolveStatus( transactionStatus: string ): MovementStatus {
	if ( transactionStatus === 'CANCELLED' ) {
		return 'cancelled'
	}
	if ( transactionStatus === 'PROCESSING' ) {
		return 'in_transit'
	}
	return 'completed'
}

/**
 * Conversions and merges label off the product on the other leg, which the Reference
 * column also shows. `sourceType` is MERGE only when the API matched the absorbed
 * product's duplicate marker to the survivor; everything else is a plain conversion.
 */
function conversionLabel( entry: ItemHistoryEntry, direction: MovementDirection ): string | null {
	if ( entry.sourceType !== ItemHistorySourceType.Conversion && entry.sourceType !== ItemHistorySourceType.Merge ) {
		return null
	}
	const isMerge = entry.sourceType === ItemHistorySourceType.Merge
	if ( direction === 'in' ) {
		return isMerge ? 'Merged in' : 'Converted in'
	}
	if ( direction === 'out' ) {
		return isMerge ? 'Merged out' : 'Converted out'
	}
	return null
}

export function deriveMovementRow( entry: ItemHistoryEntry ): MovementRow {
	const recognized = entry.transactionType in TYPE_LABELS
	let label = TYPE_LABELS[ entry.transactionType ] ?? 'Unrecognized transaction type'

	const { direction, flags: directionFlags } = resolveDirection( entry, recognized )
	const { counterparty, flags: counterpartyFlags } = resolveCounterparty( entry )
	const flags: MovementFlag[] = [ ...directionFlags, ...counterpartyFlags ]

	// The API emits STORE_TO_STORE for every transfer (there is no TRANSFER_SHIPMENT/RECEIPT
	// type). A transfer is "legacy" — predating transfer orders — only when it has NO
	// associated transfer order, which is precisely when sourceType is not TRANSFER_ORDER.
	// Keying legacy off the transaction type alone mislabelled modern transfers (which do
	// carry a TO) as legacy, and stamped them with an approximate date.
	if ( entry.transactionType === 'STORE_TO_STORE' ) {
		const hasTransferOrder = entry.sourceType === ItemHistorySourceType.TransferOrder
		if ( hasTransferOrder ) {
			label = direction === 'in' ? 'Received on transfer' : 'Shipped on transfer'
		} else {
			label = direction === 'in' ? 'Transferred in (legacy)' : 'Transferred out (legacy)'
			flags.push( 'legacy_transfer', 'approximate_date' )
		}
	}

	label = conversionLabel( entry, direction ) ?? label

	const status = resolveStatus( entry.transactionStatus )
	if ( status === 'cancelled' ) {
		flags.push( 'cancelled' )
	}
	if ( status === 'in_transit' ) {
		// An in-transit entry is dated by its creation, because nothing records the moment
		// stock left; the API re-dates it to the completion once the transfer is received.
		flags.push( 'in_transit', 'provisional_date' )
	}

	const magnitude = Math.abs( entry.quantityChange )
	const isUnchanged = direction === 'unchanged'
	const isUnknown = direction === 'unknown'
	const delta = isUnknown ? null : direction === 'out' ? -magnitude : direction === 'in' ? magnitude : 0

	if ( isUnchanged ) {
		flags.push( 'zero_delta' )
	}
	if ( DEPRECATED_TYPES.has( entry.transactionType ) && recognized ) {
		flags.push( 'deprecated_type' )
	}
	if ( entry.memo && entry.memo.length > LONG_MEMO_THRESHOLD ) {
		flags.push( 'adjustment_memo_long' )
	}

	return {
		id: entry.id,
		date: entry.dateApplied,
		transactionType: entry.transactionType,
		label,
		direction,
		status,
		delta,
		quantity: magnitude,
		unchangedQuantity: isUnchanged ? entry.quantityOrdered ?? null : null,
		counterparty,
		actor: formatActor( entry.modifiedByUser ),
		costPerUnit: entry.costPerItem ?? null,
		memo: entry.memo ?? null,
		flags,
	}
}

export function deriveMovementRows( entries: ItemHistoryEntry[] ): MovementRow[] {
	return entries.map( deriveMovementRow )
}
